import React, {useEffect, useState, useRef} from 'react'
import {
    Heading,
    Stack,
    Divider,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import ColorRefinements from './color-refinements'
import SizeRefinements from './size-refinements'
import SliderRefinements from './slider-refinements'
import CheckboxRefinements from './checkbox-refinements'
import {MinusIcon, AddIcon} from '@chakra-ui/icons'
//import LinkRefinements from '@salesforce/retail-react-app/app/pages/product-list/partials/link-refinements'
import {isServer, noop} from '@salesforce/retail-react-app/app/utils/utils'
import {FILTER_ACCORDION_SATE} from '@salesforce/retail-react-app/app/constants'
import {accordionHeaderStyles, accordionPanelContentStyles} from './refinements.style'
import IconRefinements from './icon-refinements'
import {pxToRem} from '../../../utills/pxToRem'
import CategoryLinks from './category-links'
import useGetSiblingCategoriesRefinements from '../../../hooks/get-sibling-categories-refinements'
import {useLocation} from 'react-router-dom'
import colors from '../../../theme/foundations/colors'
/** Map of refinement attribute IDs to the components used to display values as filter options. */
export const componentMap = {
    cgid: CategoryLinks,
    c_refinementColor: ColorRefinements,
    c_size: SizeRefinements,
    c_width: SizeRefinements,
    c_length: SizeRefinements,
    c_waist: SizeRefinements,
    price: SliderRefinements,
    c_boottoe: IconRefinements
}
const TOP_EXPANDED_FILTERS = ['cgid', 'brand', 'price']
const Refinements = ({
    itemsBefore,
    excludedFilters = [],
    filters = [],
    toggleFilter,
    selectedFilters,
    isLoading,
    onClose = noop
}) => {
    const location = useLocation()
    const [expandedIndexes, setExpandedIndexes] = useState([])
    const prevCategory = useRef('')

    const isCgidNotAvailable = !filters.some((filter) => filter?.attributeId === 'cgid')

    const {data: siblingCategories} = useGetSiblingCategoriesRefinements(
        location?.pathname?.split('/')[2],
        isCgidNotAvailable
    )
    const siblingCategoryRefine = siblingCategories?.refinements
    let combinedFilters = filters

    if (siblingCategoryRefine) {
        const isCgidAlreadyPresent = filters.some((filter) => filter?.attributeId === 'cgid')
        if (!isCgidAlreadyPresent) {
            const labelChangedRefine = {...siblingCategoryRefine, label: 'Category'}
            combinedFilters = [labelChangedRefine, ...filters]
        }
    }

    // Exclude filters in the exclude list.
    if (excludedFilters.length > 0) {
        combinedFilters = combinedFilters.filter(
            ({attributeId}) => !excludedFilters.includes(attributeId)
        )
    }

    // Getting the indices of filters to open accordions by default
    // itemsBefore is for displaying the category section, it won't be considered as part of filters.
    // Since we need to open only first 3 filters below logic is implemented
    // Determine which filter accordions should be open by default
    let indexes = itemsBefore ? [0, 1, 2, 3] : [0, 1, 2]

    let filtersIndexes = indexes

    // Use saved state for accordions
    if (!isServer) {
        // TODO: Change this to `useLocalStorage` hook when localStorage detection is more robust
        const filterAccordionState = window.localStorage.getItem(FILTER_ACCORDION_SATE)
        const savedExpandedAccordionIndexes =
            filterAccordionState && JSON.parse(filterAccordionState)
        if (savedExpandedAccordionIndexes) {
            filtersIndexes = filters
                ?.map((filter, index) => {
                    if (savedExpandedAccordionIndexes.includes(filter.attributeId)) {
                        return index
                    }
                })
                .filter((index) => index !== undefined)
        }
    }
    useEffect(() => {
        const currentCategory = location.pathname
        const isCategoryChanged = prevCategory.current !== currentCategory

        if (isCategoryChanged) {
            prevCategory.current = currentCategory
            const newExpandedIndexes = combinedFilters.reduce((indexes, filter, idx) => {
                if (TOP_EXPANDED_FILTERS.includes(filter.attributeId) && indexes.length < 3) {
                    indexes.push(idx)
                }
                return indexes
            }, [])

            setExpandedIndexes(newExpandedIndexes)

            if (!isServer) {
                window.localStorage.removeItem(FILTER_ACCORDION_SATE)
            }
        }
    }, [location.pathname, filters])

    // Handle saving accordion state (only for non-mobile)
    const updateAccordionState = (expandedIndex) => {
        setExpandedIndexes(expandedIndex)
        const filterState = combinedFilters
            ?.filter((_, index) => expandedIndex.includes(index))
            .map((filter) => filter.attributeId)
        // TODO: Update when localStorage detection is more robust? useLocalStorage is only a getter
        if (!isServer) {
            window.localStorage.setItem(FILTER_ACCORDION_SATE, JSON.stringify(filterState))
        }
    }

    return (
        <Stack margin={0}>
            {/* Wait to have filters before rendering the Accordion to allow the default indexes to be accurate */}
            <Accordion
                pointerEvents={isLoading ? 'none' : 'auto'}
                onChange={updateAccordionState}
                opacity={isLoading ? 0.2 : 1}
                allowMultiple={true}
                index={expandedIndexes}
                reduceMotion={true}
                borderTopWidth={pxToRem(1)}
                borderTopColor={colors.cav_pdp_dark_gray}
            >
                {combinedFilters?.map((filter, idx) => {
                    // Render the appropriate component for the refinement type, fallback to checkboxes
                    const Values = componentMap[filter.attributeId] || CheckboxRefinements
                    let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []
                    // Catch any non-array values and wrap them in an array
                    if (!Array.isArray(selectedFiltersArray)) {
                        selectedFiltersArray = [selectedFiltersArray]
                    }
                    if (
                        filter.values?.length &&
                        filter.values.filter((val) => val.hitCount > 0).length
                    ) {
                        return (
                            <Stack key={filter.attributeId} divider={<Divider />}>
                                <AccordionItem
                                    paddingTop={
                                        idx === 0
                                            ? [pxToRem(15), pxToRem(15), pxToRem(38), pxToRem(38)]
                                            : [pxToRem(25), pxToRem(25), pxToRem(38), pxToRem(38)]
                                    }
                                    border="none"
                                >
                                    {({isExpanded}) => (
                                        <>
                                            <AccordionButton
                                                h={pxToRem(20)}
                                                w={['100%']}
                                                mb={pxToRem(15)}
                                                sx={accordionHeaderStyles}
                                            >
                                                <Heading as="h2">{filter.label}</Heading>
                                                {isExpanded ? <MinusIcon /> : <AddIcon />}
                                            </AccordionButton>
                                            <AccordionPanel sx={accordionPanelContentStyles}>
                                                <Values
                                                    selectedFilters={selectedFiltersArray}
                                                    filter={filter}
                                                    toggleFilter={toggleFilter}
                                                    onClose={onClose}
                                                />
                                            </AccordionPanel>
                                        </>
                                    )}
                                </AccordionItem>
                            </Stack>
                        )
                    } else {
                        return null
                    }
                })}
            </Accordion>
        </Stack>
    )
}
Refinements.propTypes = {
    itemsBefore: PropTypes.arrayOf(PropTypes.element),
    filters: PropTypes.array,
    excludedFilters: PropTypes.arrayOf(PropTypes.string),
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    onClose: PropTypes.func
}
export default Refinements
